import type {IQuizAnswer, IQuizQuestion} from "@buildwitt/shapeshift-types";
import type {SupportedLocale} from "@/app2/shared/contexts";
import type {InlineQuizQuestionDto} from "@/app2/shared/data";

/** Inline lesson quiz question from improve-api (Orval-generated). */
export type InlineQuizApiQuestion = InlineQuizQuestionDto;

function normalizeQuestionType(type: string): IQuizQuestion["type"] {
	const aliases: Record<string, IQuizQuestion["type"]> = {
		single_choice: "singleChoice",
		multiple_choice: "multipleChoice",
		boolean: "booleanChoice",
		matching_order: "matchingOrder",
		fill_the_blank: "fillTheBlank"
	};
	return (aliases[type] ?? type) as IQuizQuestion["type"];
}

function resolveAnswerCount(q: InlineQuizQuestionDto): number | undefined {
	if (typeof q.answerCount === "number" && q.answerCount > 0) {
		return q.answerCount;
	}
	return undefined;
}

function booleanChoiceLabels(locale: SupportedLocale): [string, string] {
	return locale === "es-419" ? ["Verdadero", "Falso"] : ["True", "False"];
}

/** Extract display strings from Strapi options JSON (array, keyed object, or {id,value}[]). */
export function optionsToDisplayStrings(options: unknown): string[] | undefined {
	if (options == null) {
		return undefined;
	}
	if (Array.isArray(options)) {
		return options.map((item) => {
			if (typeof item === "string") return item;
			if (item != null && typeof item === "object") {
				const o = item as {label?: string; text?: string; value?: string};
				return o.value ?? o.label ?? o.text ?? String(item);
			}
			return String(item);
		});
	}
	if (typeof options === "object") {
		return Object.values(options as Record<string, unknown>).map((v) => {
			if (typeof v === "string") return v;
			if (v != null && typeof v === "object") {
				const o = v as {label?: string; text?: string; value?: string};
				return o.value ?? o.label ?? o.text ?? String(v);
			}
			return String(v);
		});
	}
	return undefined;
}

function pickDisplayFields(q: InlineQuizQuestionDto, locale: SupportedLocale) {
	const useLocalized = locale !== "en" && q.localized != null;
	const loc = q.localized;
	return {
		title: (useLocalized ? loc?.title : undefined) ?? q.title ?? "",
		hint: (useLocalized ? loc?.hint : undefined) ?? q.hint,
		content: (useLocalized ? loc?.content : undefined) ?? q.content,
		options: optionsToDisplayStrings(useLocalized ? loc?.options : q.options),
		words_to_blank: (useLocalized ? loc?.words_to_blank : undefined) ?? q.words_to_blank
	};
}

/**
 * Maps API questions to shapeshift format using localized copy when available.
 * English fields on the question are kept for submit mapping.
 */
export function mapApiQuestionsForDisplay(
	questions: InlineQuizQuestionDto[],
	locale: SupportedLocale
): IQuizQuestion[] {
	return questions.map((q) => {
		const display = pickDisplayFields(q, locale);
		const questionType = normalizeQuestionType(q.type);

		if (questionType === "booleanChoice") {
			const [trueLabel, falseLabel] = booleanChoiceLabels(locale);
			return {
				id: q.id,
				title: display.title,
				hint: display.hint ?? undefined,
				content: display.content ?? undefined,
				type: "singleChoice",
				options: [trueLabel, falseLabel]
			} as IQuizQuestion;
		}

		const base = {
			id: q.id,
			title: display.title,
			hint: display.hint ?? undefined,
			content: display.content ?? undefined,
			type: questionType
		} as IQuizQuestion;

		if (display.options?.length) {
			const withOptions = {...base, options: display.options} as IQuizQuestion;

			if (questionType === "multipleChoice") {
				const answerCount = resolveAnswerCount(q);
				if (answerCount != null) {
					return {...withOptions, answerCount} as IQuizQuestion;
				}
			}

			return withOptions;
		}

		if (questionType === "fillTheBlank") {
			const blankCount = (display.title.match(/_/g) || []).length;
			const answerCount = resolveAnswerCount(q) ?? (blankCount > 0 ? blankCount : undefined);
			if (answerCount != null) {
				return {
					...base,
					answerCount,
					wordsToBlank: display.words_to_blank ?? undefined
				} as IQuizQuestion;
			}
		}

		return base;
	});
}

/**
 * Submits canonical English option values. When the user picked a localized label,
 * resolves by parallel index in the display vs English option lists.
 */
export function mapSubmissionToCanonicalValues(
	apiQuestions: InlineQuizQuestionDto[],
	answers: IQuizAnswer[],
	locale: SupportedLocale
): Array<{questionId: number; selectedOptionIds: (number | boolean | string | null)[]}> {
	const byId = new Map(apiQuestions.map((q) => [q.id, q]));

	return answers.map((answer) => {
		const questionId = (answer as {questionId: number}).questionId;
		const questionType = (answer as {questionType: string}).questionType;
		const apiQ = byId.get(questionId);

		const englishOptions = optionsToDisplayStrings(apiQ?.options);
		const displayOptions = apiQ
			? optionsToDisplayStrings(
				locale !== "en" && apiQ.localized?.options != null
					? apiQ.localized.options
					: apiQ.options
			)
			: undefined;

		let answerValue: unknown;
		if (questionType === "booleanChoice") {
			answerValue = (answer as {option?: boolean}).option;
		} else if (questionType === "singleChoice") {
			answerValue = (answer as {option?: string}).option;
		} else if (questionType === "multipleChoice") {
			answerValue =
				(answer as {options?: string[]}).options ??
				(answer as {selectedOptions?: string[]}).selectedOptions;
		} else {
			answerValue = (answer as {answer?: string}).answer;
		}

		const toCanonical = (submitted: unknown): number | boolean | string | null => {
			if (submitted == null) return null;
			if (typeof submitted === "boolean") return submitted;

			if (apiQ?.type === "booleanChoice") {
				const [trueLabel, falseLabel] = booleanChoiceLabels(locale);
				if (submitted === trueLabel) return true;
				if (submitted === falseLabel) return false;
				if (submitted === "True") return true;
				if (submitted === "False") return false;
			}

			const subStr = String(submitted);
			if (englishOptions && displayOptions && englishOptions !== displayOptions) {
				const idx = displayOptions.findIndex((label) => label === subStr);
				if (idx >= 0 && englishOptions[idx] != null) {
					return englishOptions[idx];
				}
			}
			if (englishOptions?.includes(subStr)) {
				return subStr;
			}
			if (typeof submitted === "object" && submitted !== null && "id" in submitted) {
				return (submitted as {id: number | string}).id;
			}
			return submitted as string | number;
		};

		let selectedOptionIds: (number | boolean | string | null)[];

		if (Array.isArray(answerValue)) {
			selectedOptionIds = answerValue.map((v) => toCanonical(v));
		} else if (answerValue !== undefined && answerValue !== null) {
			selectedOptionIds = [toCanonical(answerValue)];
		} else {
			selectedOptionIds = [];
		}

		return {questionId, selectedOptionIds};
	});
}

/**
 * Maps graded values from the API (canonical English option strings or booleans) to the
 * labels shown in the UI for `locale`, so marked-result styling matches `mapApiQuestionsForDisplay`.
 */
export function mapCanonicalToDisplayValue(
	apiQ: InlineQuizQuestionDto | undefined,
	value: string | number | boolean | null | undefined,
	locale: SupportedLocale
): string | undefined {
	if (value === null || value === undefined) return undefined;
	if (typeof value === "boolean") {
		if (apiQ?.type === "booleanChoice") {
			const [trueLabel, falseLabel] = booleanChoiceLabels(locale);
			return value ? trueLabel : falseLabel;
		}
		return undefined;
	}
	if (apiQ == null) return String(value);
	const english = optionsToDisplayStrings(apiQ.options);
	const display = optionsToDisplayStrings(
		locale !== "en" && apiQ.localized?.options != null ? apiQ.localized.options : apiQ.options
	);
	const str = String(value);
	if (!english?.length || !display?.length) return str;
	const idx = english.findIndex((o) => o === str);
	if (idx >= 0 && display[idx] !== undefined) return display[idx];
	return str;
}
